import { useState, useCallback, useRef } from 'react';

/**
 * useStreaming - React Hook for OpenAI streaming responses
 * 
 * Handles progressive text streaming from backend with proper state management,
 * error handling, and cleanup. Compatible with mobile and desktop browsers.
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onComplete - Callback when streaming completes
 * @param {Function} options.onError - Callback on error
 * @param {Function} options.onStart - Callback when streaming starts
 * @returns {Object} Streaming state and control functions
 * 
 * @example
 * const { streamedText, isStreaming, startStream, cancelStream } = useStreaming({
 *   onComplete: (fullText) => console.log('Done:', fullText),
 *   onError: (error) => console.error('Error:', error)
 * });
 * 
 * // Start streaming
 * await startStream('/api/ai/text-training-session/stream', {
 *   method: 'POST',
 *   body: JSON.stringify({ input_text: 'Hello Owen', ...})
 * });
 */
export const useStreaming = (options = {}) => {
  const { onComplete, onError, onStart, onChunk } = options; // 🎯 PHASE 3: Add onChunk callback

  // State
  const [streamedText, setStreamedText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ Licences: 0, duration: 0 });

  // Refs for cleanup
  const abortControllerRef = useRef(null);
  const startTimeRef = useRef(null);
  const tokenCountRef = useRef(0);
  const firstTokenTimeRef = useRef(null); // 🎯 PHASE 1: Track first token time

  /**
   * Start streaming from a given endpoint
   * 
   * @param {string} url - API endpoint URL
   * @param {Object} fetchOptions - Fetch options (method, headers, body)
   * @returns {Promise<string>} Full streamed text
   */
  const startStream = useCallback(async (url, fetchOptions = {}) => {
    console.log('[useStreaming] 🚀 DIAGNOSTIC: Starting stream to URL:', url);
    console.log('[useStreaming] 🔍 DIAGNOSTIC: onChunk callback defined?', typeof onChunk === 'function');
    
    // 🔍 DIAGNOSTIC P0: Log complete request details
    console.log('[useStreaming] 🔍 DIAGNOSTIC P0 - Request Details:');
    console.log('[useStreaming]   - URL:', url);
    console.log('[useStreaming]   - Method:', fetchOptions.method || 'GET');
    console.log('[useStreaming]   - Body length:', fetchOptions.body ? fetchOptions.body.length : 0);
    console.log('[useStreaming]   - Headers:', fetchOptions.headers);
    
    // Reset state
    setStreamedText('');
    setIsStreaming(true);
    setError(null);
    setProgress({ Licences: 0, duration: 0 });
    tokenCountRef.current = 0;
    startTimeRef.current = Date.now();
    firstTokenTimeRef.current = null; // 🎯 PHASE 1: Reset first token time

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    try {
      // Call onStart callback
      if (onStart) {
        onStart();
      }

      // ✅ CRITICAL FIX: Use native fetch to avoid rrweb interception and Response clone errors
      const nativeFetch = window.__nativeFetch__ || window.fetch.bind(window);
      console.log('[useStreaming] 🔍 DIAGNOSTIC P0: Calling nativeFetch NOW...');
      const response = await nativeFetch(url, {
        ...fetchOptions,
        signal: abortControllerRef.current.signal,
        headers: {
          'Content-Type': 'application/json',
          ...fetchOptions.headers,
        },
      });
      
      console.log('[useStreaming] ✅ DIAGNOSTIC P0: nativeFetch returned, status:', response.status);
      console.log('[useStreaming] 🔍 DIAGNOSTIC P0: Response headers:', Object.fromEntries(response.headers.entries()));
      console.log('[useStreaming] 🔍 DIAGNOSTIC P0: Response body exists:', !!response.body);

      // Check response status
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      // Check if body is readable (streaming support)
      if (!response.body) {
        throw new Error('ReadableStream not supported in this browser');
      }

      const decoder = new TextDecoder('utf-8');
      let fullText = '';

      // Read stream chunks
      console.log('[useStreaming] 📖 DIAGNOSTIC: Starting to read stream chunks...');
      let chunkCount = 0;
      let reader = null;
      
      try {
        // Get reader for streaming
        reader = response.body.getReader();
        
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log('[useStreaming] ✅ DIAGNOSTIC: Stream completed. Total chunks:', chunkCount);
            break;
          }

          chunkCount++;
          console.log('[useStreaming] 📦 DIAGNOSTIC: Received chunk #', chunkCount, 'size:', value?.length || 0);

          // Decode chunk
          const chunk = decoder.decode(value, { stream: true });
          console.log('[useStreaming] 📝 DIAGNOSTIC: Decoded chunk:', chunk.substring(0, 50), '...');
          fullText += chunk;
          tokenCountRef.current += chunk.length;
          
          // 🎯 PHASE 1 - PERFORMANCE DIAGNOSTIC: Track first token (TTFT)
          if (firstTokenTimeRef.current === null && chunk.length > 0) {
            firstTokenTimeRef.current = Date.now();
            const ttft = firstTokenTimeRef.current - startTimeRef.current;
            console.log(`[PERF-FRONTEND] [FIRST_TOKEN_RECEIVED] Time: ${firstTokenTimeRef.current} | TTFT: ${ttft}ms`);
          }

          // Update state with progressive text
          setStreamedText(fullText);
          
          // 🎯 DIAGNOSTIC: Call onChunk callback for progressive updates
          console.log('[useStreaming] 🔍 DIAGNOSTIC: About to call onChunk. Defined?', typeof onChunk === 'function', 'Chunk length:', chunk.length);
          if (onChunk && chunk.length > 0) {
            console.log('[useStreaming] ✅ DIAGNOSTIC: Calling onChunk callback NOW with fullText length:', fullText.length);
            onChunk(chunk, fullText);
            console.log('[useStreaming] ✅ DIAGNOSTIC: onChunk callback completed');
          } else if (!onChunk) {
            console.error('[useStreaming] ❌ DIAGNOSTIC: onChunk callback is NOT defined!');
          } else {
            console.log('[useStreaming] ⚠️ DIAGNOSTIC: Chunk is empty, skipping callback');
          }

          // Update progress
          const duration = (Date.now() - startTimeRef.current) / 1000;
          setProgress({
            Licences: tokenCountRef.current,
            duration: duration,
          });
        }

        // Streaming completed successfully
        setIsStreaming(false);

        // Guard: si le stream n'a retourné aucune donnée, lancer une erreur pour activer le fallback
        if (!fullText || !fullText.trim()) {
          console.warn('[useStreaming] ⚠️ Stream completed but fullText is empty — triggering fallback');
          const emptyError = new Error('Stream returned empty response');
          if (onError) onError(emptyError);
          throw emptyError;
        }

        // Call onComplete callback
        if (onComplete) {
          onComplete(fullText);
        }

        return fullText;

      } catch (err) {
        // Check if error is from cancellation
        if (err.name === 'AbortError') {
          console.log('🛑 Stream cancelled by user');
          setIsStreaming(false);
          return streamedText; // Return partial text
        }

        // Handle other errors
        console.error('❌ Streaming error:', err);
        setError(err.message || 'Streaming error occurred');
        setIsStreaming(false);

        // Call onError callback
        if (onError) {
          onError(err);
        }

        throw err;
      } finally {
        // 🔧 FIX STREAMING: Toujours relâcher le reader
        if (reader) {
          try {
            reader.releaseLock();
            console.log('[useStreaming] ✅ Reader lock released');
          } catch (releaseErr) {
            console.warn('[useStreaming] ⚠️ Could not release reader:', releaseErr);
          }
        }
        
        // 🔧 FIX STREAMING: Garantir que isStreaming est false
        setIsStreaming(false);
      }
    } catch (err) {
      // Top-level error handling (fetch errors, etc.)
      console.error('❌ Top-level streaming error:', err);
      setError(err.message || 'Streaming error occurred');
      setIsStreaming(false);

      // Call onError callback
      if (onError) {
        onError(err);
      }

      throw err;
    }
  }, [onComplete, onError, onStart, streamedText]);

  /**
   * Cancel ongoing stream
   */
  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  /**
   * Reset streaming state
   */
  const resetStream = useCallback(() => {
    setStreamedText('');
    setIsStreaming(false);
    setError(null);
    setProgress({ Licences: 0, duration: 0 });
    tokenCountRef.current = 0;
    startTimeRef.current = null;
    firstTokenTimeRef.current = null; // 🎯 PHASE 1: Reset first token time

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  return {
    // State
    streamedText,
    isStreaming,
    error,
    progress,

    // Controls
    startStream,
    cancelStream,
    resetStream,
  };
};

/**
 * useStreamingWithFallback - Streaming hook with automatic fallback to non-streaming
 * 
 * Tries streaming first, falls back to traditional request if streaming fails
 * 
 * @param {Object} options - Configuration options
 * @returns {Object} Streaming state and control functions with fallback support
 */
export const useStreamingWithFallback = (options = {}) => {
  const streaming = useStreaming(options);
  const [fallbackMode, setFallbackMode] = useState(false);

  const startStreamWithFallback = useCallback(async (streamUrl, nonStreamUrl, fetchOptions = {}) => {
    try {
      // Try streaming first
      return await streaming.startStream(streamUrl, fetchOptions);
    } catch (err) {
      console.warn('⚠️ Streaming failed, falling back to non-streaming:', err);
      setFallbackMode(true);

      // ✅ CRITICAL FIX: Use native fetch to avoid rrweb interception and Response clone errors
      const nativeFetch = window.__nativeFetch__ || window.fetch.bind(window);
      const response = await nativeFetch(nonStreamUrl, fetchOptions);
      if (!response.ok) {
        throw new Error(`Fallback request failed: ${response.status}`);
      }
      const data = await response.json();
      return data.reply_text || data.response || '';
    }
  }, [streaming]);

  return {
    ...streaming,
    fallbackMode,
    startStreamWithFallback,
  };
};

export default useStreaming;
